import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useStore } from '../store'
import { Header } from '../components/shell'
import { QueueRail } from '../components/rail'
import { ThreadPane } from '../components/thread'
import { ContextPane } from '../components/context'
import { MobileConsolePage } from './MobileConsolePage'
import { useIsMobile } from '../lib/viewport'
import { modalReads } from '../components/modals/reads'
import { composePrefillErrorText } from '../components/modals/format'

/**
 * The console shell — Task 27 (console integration). The header (Task 13)
 * plus the three real panes: A (Task 14's `QueueRail`, self-sized to
 * 340px as of task 3's Tossker repaint — up from 272px, to fit the call
 * floor's cards), B (Task 15's `ThreadPane`, the flexible centre), and C (Task 16's
 * `ContextPane`, self-sized to 264px open / 38px collapsed). Each pane reads
 * the shared store directly (no props threaded through here) — exactly the
 * "zero required props, reads useStore itself" convention every one of
 * those tasks' own reports already established, so this page is pure
 * composition, not a new prop-drilling layer.
 *
 * Bringing the store to life (the initial threads/presence/identity fetch
 * and the live SSE subscription) is deliberately NOT done here — see
 * src/bootstrap.ts and App.tsx's own comments for why that has to run once
 * for the whole app's lifetime rather than on every mount of this page.
 */
/**
 * Open the thread a link asked for.
 *
 * HALO'S DEEP LINKS LANDED HERE AND DID NOTHING. Every ticket note the outbox
 * writes carries a link back to the conversation — `/transcript/<id>` for a
 * closed thread, which has a route, and `/?thread=<id>` for a live one, which
 * nothing on this page ever read. So the "live thread" half of that feature
 * had never worked: it took you to the console and left you to find the
 * conversation yourself. Andrew found it after the domain move, but it was
 * not a regression — it had never worked.
 *
 * The id is not validated here beyond being present. `selectThread` sets the
 * active id and `ThreadPane` fetches that thread by id, so a stale or
 * unknown id resolves exactly like any other missing thread — and crucially,
 * a thread OUTSIDE the rail's current filter still opens, which is the normal
 * case for a link to something closed, parked, or owned by somebody else.
 *
 * The parameter is cleared once applied. Left in place it would re-select
 * that thread on every reload, quietly overriding whatever the person had
 * clicked since.
 */
function useThreadFromLink(): void {
  const [params, setParams] = useSearchParams()
  const selectThread = useStore((s) => s.selectThread)
  const requested = params.get('thread')

  useEffect(() => {
    if (!requested) return
    selectThread(requested)
    const next = new URLSearchParams(params)
    next.delete('thread')
    setParams(next, { replace: true })
    // `params`/`setParams` are new objects on every render; keying on the id
    // is what makes this run once per link rather than forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested])
}

/**
 * Open the Compose modal a Halo ticket's "New message" button asked for —
 * `?compose=1` with an optional `&ticket=<id>` alongside it, Task 8.
 *
 * Same idiom as `useThreadFromLink` above: read the params, act once, strip
 * them so a reload doesn't replay the link. The one wrinkle here is that
 * there are two things to do, on two different timescales. Opening the
 * composer is immediate and synchronous — a tech who followed a link from a
 * ticket should see the modal with that ticket attached with no visible
 * delay, not a blank composer that pops the ticket in a moment later.
 * `modalReads.composePrefill` (a real Halo round-trip, task 6's read) only
 * adds the CONTACT on top, once it answers.
 *
 * `wired: false` is the spec's own error-handling case (finding 4 — the composer must still
 * open, the ticket slot handled honestly, an error shown inline), but "Halo unreachable" and
 * "this ticket does not exist" are different fact patterns and get different treatment:
 *
 *   - unreachable/not-configured — the ticket id might still be perfectly good, so the chip
 *     stays exactly as first opened, editable same as always. A tech who clicked a real link
 *     from a real ticket should never be told "that didn't work" just because the prefetch
 *     failed to ask.
 *   - not-found — Halo DID answer, and it does not have this ticket. Leaving the chip in place
 *     would let the tech send against an id that 400s on the very next Halo write, so the chip
 *     is cleared instead — same honesty rule as `link-ticket`'s own boundary normalisation, just
 *     applied before the write is even attempted rather than after Halo refuses it.
 *
 * Either way the reason reaches the composer via `composePrefill.error` (store.ts), rendered as
 * an inline `role="alert"` in ComposeModal — never a silent empty slot.
 *
 * The prefetch's `.then()` runs on Halo's clock, not ours — by the time it answers, the tech may
 * already have closed the composer, or (if `openCompose` is ever called again for something
 * else before this resolves) be looking at a different compose session entirely. Firing
 * `openCompose` unconditionally there was review finding Critical #2: a stale response could
 * silently REOPEN a modal the tech had already dismissed, or clobber whatever they'd moved on
 * to. The guard reads live state at resolve time (not whatever was captured when the fetch was
 * fired) and only applies the upgrade while it is still unambiguously this same, still-open,
 * still-unanswered compose session — `composePrefill.contact` still `null` is what "still
 * unanswered" looks like, since nothing else in this flow ever writes a contact into the store
 * before this `.then()` does.
 */
function useComposeFromLink(): void {
  const [params, setParams] = useSearchParams()
  const openCompose = useStore((s) => s.openCompose)
  const compose = params.get('compose')
  const ticket = params.get('ticket')

  useEffect(() => {
    if (compose !== '1') return

    openCompose({ ticketId: ticket ?? null })

    if (ticket) {
      void modalReads.composePrefill(ticket).then((result) => {
        // Same guard either way: only apply this to the still-open, still-unanswered session
        // this fetch was fired for — see the doc comment above (Critical #2's fix).
        const current = useStore.getState()
        const stillThisSession =
          current.modal === 'compose' &&
          current.composePrefill?.ticketId === ticket &&
          current.composePrefill?.contact === null
        if (!stillThisSession) return

        if (result.wired) {
          current.openCompose({ ticketId: ticket, contact: result.contact ?? null })
          return
        }

        // `wired: false` — the ticket link is unresolvable, one way or another. See the doc
        // comment above for why 'not-found' clears the chip and everything else keeps it.
        const error = composePrefillErrorText(result.reason)
        if (result.reason === 'not-found') {
          current.openCompose({ ticketId: null, contact: null, error })
        } else {
          current.openCompose({ ticketId: ticket, contact: null, error })
        }
      })
    }

    const next = new URLSearchParams(params)
    next.delete('compose')
    next.delete('ticket')
    setParams(next, { replace: true })
    // `params`/`setParams` are new objects on every render; keying on the two values read from
    // them is what makes this run once per link rather than forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compose, ticket])
}

export function ConsolePage() {
  useThreadFromLink()
  useComposeFromLink()

  // A phone gets a different shell entirely, not this one squeezed: see
  // MobileConsolePage. Chosen here so every caller keeps rendering one page and
  // neither layout has to know the other exists.
  const isMobile = useIsMobile()
  if (isMobile) return <MobileConsolePage />

  return (
    <div className="flex h-full min-h-0 flex-col bg-page">
      <Header />
      <div className="flex flex-1 min-h-0">
        <QueueRail />
        <ThreadPane />
        <ContextPane />
      </div>
    </div>
  )
}
